import {z} from 'zod'

export const UserStatus = z.enum(['active_available', 'active_unavailable', 'suspended', 'deactivated'])
export type UserStatus = z.infer<typeof UserStatus>

/**
 * Current user, as returned by the Node `/v3/navigate/photographers/me` endpoint
 * (camelCase). This is the canonical shape stored in the `[ME]` query cache and
 * consumed across the app.
 */
export const User = z.object({
    airbnbUserId: z.string().nullish(),
    description: z.string().nullable(),
    address: z.string().nullable(),
    country: z.string().nullable(),
    email: z.string().min(1),
    airbnbEmail: z.string().min(1).nullish(),
    newEmail: z.string().min(1).nullish(),
    firstName: z.string().min(1),
    lastName: z.string().nullish(),
    id: z.number().int().positive(),
    optinSms: z.boolean(),
    sendNotificationsToPhone: z.coerce.boolean(),
    photographerGeos: z.array(
        z.object({
            geoId: z.number().int().positive(),
            id: z.number().int().positive(),
            isMain: z.boolean(),
            geo: z.object({
                country: z.string().min(1),
                extendedCountry: z.string().min(1),
                id: z.number().int().positive(),
                geo: z.string().min(1),
                region: z.string().min(1)
            })
        })
    ),
    profilePhotoUrl: z.string().url().nullable(),
    region: z.string().min(1).nullable(),
    shootAvailable: z.coerce.boolean(),
    phone: z.string().min(1).nullable(),
    isInspector: z.boolean().nullable(),
    isCreator: z.boolean().nullable(),
    latitude: z.string().min(1).nullable(),
    longitude: z.string().min(1).nullable(),
    status: UserStatus,
    calendar: z
        .object({
            isConnectedToGoogle: z.boolean(),
            icsUrl: z.string().min(1).nullable(),
            googleEmail: z.string().min(1).nullable(),
            isGoogleConnectionExpired: z.boolean()
        })
        .optional(),
    isAirbnbSsoConfigured: z.boolean(),
    isAirbnbSsoActive: z.boolean(),
    lastMarketsUpdatedAt: z.string().min(1).nullable(),
    // Floor-plan opt-in flag (true = opted in). Lives on the photographer me.
    is3dScanEligible: z.boolean().nullish(),
    // Whether the insights section is visible to this user (from the node /me).
    isInsightsVisible: z.boolean().nullish(),
    // Whether the floor plan section is visible. Only visible to photographers.
    isFloorPlanVisible: z.boolean().nullish(),
    // Whether the photographer is paid via Upwork (from the node /me).
    isPaidViaUpwork: z.boolean().nullish(),
    // Whether the photographer is in the AI chatbot test group. Temporary rollout
    // flag, set directly in the database; goes away once the chatbot ships to all.
    isChatbotTester: z.boolean().nullish(),
    // Whether the freelancer can be assigned reviews (from the node /me).
    isReviewer: z.boolean().nullish()
})
export type User = z.infer<typeof User>

export type PhotographerGeo = User['photographerGeos'][number]

/**
 * Legacy snake_case user shape returned by the PHP backend (rate, settings PATCH,
 * admin status, login). These endpoints still answer in snake_case, so we validate
 * their responses with this schema and remap onto the camelCase `User` before
 * writing to the `[ME]` cache. New code should use `User`.
 */
export const PhpUser = z.object({
    airbnb_user_id: z.string().nullish(),
    description: z.string().nullable(),
    address: z.string().nullable(),
    country: z.string().nullable(),
    email: z.string().min(1),
    airbnb_email: z.string().min(1).nullish(),
    new_email: z.string().min(1).nullish(),
    first_name: z.string().min(1),
    last_name: z.string().min(1).nullable(),
    id: z.number().int().positive(),
    optin_sms: z.boolean(),
    send_notifications_to_phone: z.coerce.boolean(),
    photographer_geos: z.array(
        z.object({
            geo_id: z.number().int().positive(),
            id: z.number().int().positive(),
            is_main: z.boolean(),
            geo: z.object({
                country: z.string().min(1),
                extended_country: z.string().min(1),
                id: z.number().int().positive(),
                geo: z.string().min(1),
                region: z.string().min(1)
            })
        })
    ),
    profile_photo_url: z.string().url().nullable(),
    region: z.string().min(1).nullable(),
    shoot_available: z.coerce.boolean(),
    phone: z.string().min(1).nullable(),
    is_inspector: z.boolean().nullable(),
    is_creator: z.boolean().nullable(),
    latitude: z.string().min(1).nullable(),
    longitude: z.string().min(1).nullable(),
    status: UserStatus,
    calendar: z
        .object({
            is_connected_to_google: z.boolean(),
            ics_url: z.string().min(1).nullable(),
            google_email: z.string().min(1).nullable(),
            is_google_connection_expired: z.boolean()
        })
        .optional(),
    is_airbnb_sso_configured: z.boolean(),
    is_airbnb_sso_active: z.boolean(),
    last_markets_updated_at: z.string().min(1).nullable()
})
export type PhpUser = z.infer<typeof PhpUser>

/** Maps the PHP snake_case photographer geos onto the camelCase `User` shape. */
export const mapPhpPhotographerGeos = (geos: PhpUser['photographer_geos']): User['photographerGeos'] =>
    geos.map(g => ({
        geoId: g.geo_id,
        id: g.id,
        isMain: g.is_main,
        geo: {
            country: g.geo.country,
            extendedCountry: g.geo.extended_country,
            id: g.geo.id,
            geo: g.geo.geo,
            region: g.geo.region
        }
    }))

/** Maps a full PHP snake_case user response onto the camelCase `User` shape. */
export const phpUserToUser = (u: PhpUser): User => ({
    airbnbUserId: u.airbnb_user_id,
    description: u.description,
    address: u.address,
    country: u.country,
    email: u.email,
    airbnbEmail: u.airbnb_email,
    newEmail: u.new_email,
    firstName: u.first_name,
    lastName: u.last_name,
    id: u.id,
    optinSms: u.optin_sms,
    sendNotificationsToPhone: u.send_notifications_to_phone,
    photographerGeos: mapPhpPhotographerGeos(u.photographer_geos),
    profilePhotoUrl: u.profile_photo_url,
    region: u.region,
    shootAvailable: u.shoot_available,
    phone: u.phone,
    isInspector: u.is_inspector,
    isCreator: u.is_creator,
    latitude: u.latitude,
    longitude: u.longitude,
    status: u.status,
    calendar: u.calendar && {
        isConnectedToGoogle: u.calendar.is_connected_to_google,
        icsUrl: u.calendar.ics_url,
        googleEmail: u.calendar.google_email,
        isGoogleConnectionExpired: u.calendar.is_google_connection_expired
    },
    isAirbnbSsoConfigured: u.is_airbnb_sso_configured,
    isAirbnbSsoActive: u.is_airbnb_sso_active,
    lastMarketsUpdatedAt: u.last_markets_updated_at
})

/** Maps a partial PHP snake_case user (e.g. the login response) onto a partial camelCase `User`. */
export const phpUserToUserPartial = (u: Partial<PhpUser>): Partial<User> => ({
    ...(u.airbnb_user_id !== undefined && {airbnbUserId: u.airbnb_user_id}),
    ...(u.description !== undefined && {description: u.description}),
    ...(u.address !== undefined && {address: u.address}),
    ...(u.country !== undefined && {country: u.country}),
    ...(u.email !== undefined && {email: u.email}),
    ...(u.airbnb_email !== undefined && {airbnbEmail: u.airbnb_email}),
    ...(u.new_email !== undefined && {newEmail: u.new_email}),
    ...(u.first_name !== undefined && {firstName: u.first_name}),
    ...(u.last_name !== undefined && {lastName: u.last_name}),
    ...(u.id !== undefined && {id: u.id}),
    ...(u.optin_sms !== undefined && {optinSms: u.optin_sms}),
    ...(u.send_notifications_to_phone !== undefined && {
        sendNotificationsToPhone: u.send_notifications_to_phone
    }),
    ...(u.photographer_geos !== undefined && {photographerGeos: mapPhpPhotographerGeos(u.photographer_geos)}),
    ...(u.profile_photo_url !== undefined && {profilePhotoUrl: u.profile_photo_url}),
    ...(u.region !== undefined && {region: u.region}),
    ...(u.shoot_available !== undefined && {shootAvailable: u.shoot_available}),
    ...(u.phone !== undefined && {phone: u.phone}),
    ...(u.is_inspector !== undefined && {isInspector: u.is_inspector}),
    ...(u.is_creator !== undefined && {isCreator: u.is_creator}),
    ...(u.latitude !== undefined && {latitude: u.latitude}),
    ...(u.longitude !== undefined && {longitude: u.longitude}),
    ...(u.status !== undefined && {status: u.status}),
    ...(u.calendar !== undefined && {
        calendar: u.calendar && {
            isConnectedToGoogle: u.calendar.is_connected_to_google,
            icsUrl: u.calendar.ics_url,
            googleEmail: u.calendar.google_email,
            isGoogleConnectionExpired: u.calendar.is_google_connection_expired
        }
    }),
    ...(u.is_airbnb_sso_configured !== undefined && {isAirbnbSsoConfigured: u.is_airbnb_sso_configured}),
    ...(u.is_airbnb_sso_active !== undefined && {isAirbnbSsoActive: u.is_airbnb_sso_active}),
    ...(u.last_markets_updated_at !== undefined && {lastMarketsUpdatedAt: u.last_markets_updated_at})
})

export const PhotographerUser = z.object({
    email: z.string().min(1),
    first_name: z.string().min(1),
    last_name: z.string().min(1).nullable(),
    id: z.coerce.number().int().positive(),
    picture_url: z.string().url().nullable()
})

export type PhotographerUser = z.infer<typeof PhotographerUser>
